// @purpose STATE
// Full-test client (M3.2). Loaded only on /profile/. Uses the htmx + Alpine instances main.js already
// started (window.htmx / window.Alpine), so this bundle stays small.
//  - registers the test's Alpine components, then initialises #test-region (it ships x-ignore'd so
//    Alpine never sees a component name before it exists, on first load or after a boosted swap);
//  - adds latency / motion mode / render tier to every answer POST (S5.5, 22b §5);
//  - offline: an answer that can't be sent goes to the IndexedDB queue, a banner shows, and the
//    queue flushes in order on reconnect -- the server's idempotency key makes re-sends safe;
//  - keyboard: 1-9 pick, Enter next, Esc pause; after every swap the new screen's card scrolls to the
//    top (under the focus bar) and focus moves to its heading (audit v3 F2, focus.js);
//  - analytics: test_started / quad_answered / test_paused via the generated, schema-checked track().
import { track } from "../track.js";
import { COMPONENTS } from "./components.js";
import { focusScreen } from "./focus.js";
import { applyMode, currentMode, cycleMode } from "./motion.js";
import { enqueue, flush, pending } from "./queue.js";

const RENDER_TIER = "3"; // tier 3 = 2D SVG (the only tier until the 3D specimen ships, M4)
const bootedAt = Date.now();
let registered = false;

const $ = (sel, root = document) => root.querySelector(sel);
const region = () => document.getElementById("test-region");

function registerComponents() {
  const Alpine = window.Alpine;
  if (registered || !Alpine) return !!Alpine;
  for (const [name, fn] of Object.entries(COMPONENTS)) Alpine.data(name, fn);
  registered = true;
  return true;
}

function initRegion() {
  const el = region();
  if (!el || !el.hasAttribute("x-ignore") || !registerComponents()) return;
  el.removeAttribute("x-ignore");
  delete el._x_ignore;
  window.Alpine.initTree(el);
}

function setStatus(text) {
  const box = $("[data-save-status]", region() || document);
  if (box) box.textContent = text;
}

function copy(key) {
  const el = region();
  return (el && el.dataset[key]) || "";
}

function showOffline(on) {
  const banner = document.getElementById("test-offline");
  if (banner) banner.hidden = !on;
}

// ------------------------------------------------------------------ answer telemetry + tracking

function isAnswerForm(elt) {
  return elt && elt.matches && elt.matches("[data-answer-form]");
}

function telemetry(form) {
  const shown = Number(form.dataset.shownAt || Date.now());
  return {
    latency_ms: String(Math.max(0, Date.now() - shown)),
    motion_mode: currentMode(),
    render_tier: RENDER_TIER,
  };
}

function quadProps(form, params) {
  const step = form.closest("[data-step]");
  return {
    quad_id: String(params.get ? params.get("item") : params.item),
    shown_order: Number(step ? step.dataset.q : 0),
    most: String(params.get ? params.get("most") : params.most),
    least: String(params.get ? params.get("least") : params.least),
    latency_ms: Number(params.get ? params.get("latency_ms") : params.latency_ms),
  };
}

function formBody(form) {
  const body = new URLSearchParams(new FormData(form));
  for (const [k, v] of Object.entries(telemetry(form))) body.set(k, v);
  return body;
}

async function queueAnswer(form) {
  const body = formBody(form);
  await enqueue({
    key: `${body.get("item")}:${body.get("attempt")}`,
    url: form.getAttribute("hx-post"),
    body: body.toString(),
    quad: form.hasAttribute("data-quad") ? quadProps(form, body) : null,
  });
  setStatus(`✓ ${copy("savedOffline")}`);
  showOffline(true);
}

// ------------------------------------------------------------------ flush on reconnect

function csrf() {
  try {
    return JSON.parse(document.body.getAttribute("hx-headers") || "{}")["X-CSRFToken"] || "";
  } catch {
    return "";
  }
}

async function send(entry) {
  try {
    const res = await fetch(entry.url, {
      method: "POST",
      body: entry.body,
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "HX-Request": "true",
        "HX-Target": "test-region",
        "X-CSRFToken": csrf(),
      },
    });
    if (res.status >= 500) return null;
    if (entry.quad) track("quad_answered", entry.quad);
    return res;
  } catch {
    return null; // still offline: keep the entry, keep the order
  }
}

async function showResponse(res) {
  const location = res.headers.get("HX-Location");
  if (location) {
    await window.htmx.ajax("GET", location, { target: "body", swap: "innerHTML" });
    history.pushState({ htmx: true }, "", location);
    return;
  }
  const html = await res.text();
  if (html && region()) window.htmx.swap(region(), html, { swapStyle: "innerHTML" });
  const push = res.headers.get("HX-Push-Url");
  if (push) history.pushState({ htmx: true }, "", push);
}

async function flushQueue() {
  if (!navigator.onLine || !(await pending()).length) return;
  setStatus(copy("backOnline"));
  const last = await flush(send);
  if (last) await showResponse(last);
  showOffline(!navigator.onLine || (await pending()).length > 0);
}

// ------------------------------------------------------------------ htmx hooks

document.addEventListener("htmx:configRequest", (e) => {
  const elt = e.detail.elt;
  if (isAnswerForm(elt)) {
    Object.assign(e.detail.parameters, telemetry(elt));
    if (elt.hasAttribute("data-quad")) elt._calmTrack = quadProps(elt, e.detail.parameters);
  } else if (elt && elt.matches && elt.matches("[data-pause]")) {
    const step = $("[data-step]", region() || document);
    elt._calmTrack = { last_quad: step ? step.dataset.item || "" : "", time_ms: Date.now() - bootedAt };
  }
});

document.addEventListener("htmx:beforeRequest", (e) => {
  const elt = e.detail.elt;
  if (!isAnswerForm(elt)) return;
  if (!navigator.onLine) {
    e.preventDefault();
    queueAnswer(elt);
    return;
  }
  setStatus(copy("saving"));
});

// A network failure on an answer is not an error for the person: queue it instead of showing
// the generic retry notice (errors.js listens on document; stop it at <body>).
document.body.addEventListener("htmx:sendError", (e) => {
  const elt = e.detail.elt;
  if (!isAnswerForm(elt)) return;
  e.stopPropagation();
  queueAnswer(elt);
});

document.addEventListener("htmx:afterRequest", (e) => {
  const { elt, successful } = e.detail;
  if (!successful || !elt || !elt._calmTrack) return;
  if (isAnswerForm(elt)) track("quad_answered", elt._calmTrack);
  else if (elt.matches("[data-pause]") && elt._calmTrack.last_quad) track("test_paused", elt._calmTrack);
  elt._calmTrack = null;
});

document.addEventListener("htmx:afterSwap", (e) => {
  if (e.detail.target !== region()) return;
  const step = $("[data-step]", region());
  if (step && step.dataset.started === "1") {
    track("test_started", { test_type: "full", motion_mode: currentMode(), arm: "default" });
  }
});

document.addEventListener("htmx:afterSettle", (e) => {
  if (e.detail.target !== region()) return;
  applyMode();
  focusScreen(region(), currentMode() === "full");
});

document.addEventListener("htmx:load", initRegion);
document.addEventListener("htmx:historyRestore", initRegion);

// ------------------------------------------------------------------ keyboard

function activeForm() {
  return $("#test-region form[data-answer-form]", document);
}

document.addEventListener("keydown", (e) => {
  if (!document.getElementById("test-root") || e.defaultPrevented) return;
  if (e.altKey || e.ctrlKey || e.metaKey) return;
  if (e.key === "Escape") {
    const pause = $("[data-pause]");
    if (pause) {
      e.preventDefault();
      pause.click();
    }
    return;
  }
  const form = activeForm();
  if (!form) return;
  if (/^[1-9]$/.test(e.key)) {
    const btn = $(`[data-key="${e.key}"]`, form);
    if (btn) {
      e.preventDefault();
      btn.click();
      btn.focus();
    }
  } else if (e.key === "Enter") {
    const next = $("[data-next], button[type=submit]", form);
    if (next && !next.disabled) {
      e.preventDefault();
      form.requestSubmit(next);
    }
  }
});

// ------------------------------------------------------------------ motion + connectivity

document.addEventListener("click", (e) => {
  const toggle = e.target.closest && e.target.closest("[data-motion-toggle]");
  if (toggle) cycleMode();
});

window.addEventListener("offline", () => showOffline(true));
window.addEventListener("online", () => {
  showOffline(false);
  flushQueue();
});

applyMode();
initRegion();
showOffline(!navigator.onLine);
flushQueue();
